import React, { useState, useEffect } from 'react';
import { plants, properties, photos } from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import GardenModal from './GardenModal';
import './AddPlantForm.css';

function AddPlantForm() {
  const [userProperties, setUserProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modal, setModal] = useState({ isOpen: false });
  const [formData, setFormData] = useState({
    propertyId: '',
    commonName: '',
    scientificName: '',
    plantType: '',
    datePlanted: '',
    notes: '',
    latitude: '',
    longitude: ''
  });
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const prefilledData = location.state?.prefilledData;

  useEffect(() => { loadProperties(); }, []);

  useEffect(() => {
    if (prefilledData) {
      setFormData(prev => ({
        ...prev,
        commonName: prefilledData.commonName || '',
        scientificName: prefilledData.scientificName || '',
        plantType: prefilledData.plantType || ''
      }));
    }
  }, [prefilledData]);

  const loadProperties = async () => {
    try {
      const response = await properties.getAll();
      setUserProperties(response.data.properties);
      if (response.data.properties.length > 0) {
        const firstProp = response.data.properties[0];
        setFormData(prev => ({ ...prev, propertyId: firstProp.id }));
        if (firstProp.parcel_boundary?.coordinates) {
          const coords = firstProp.parcel_boundary.coordinates[0];
          const lats = coords.map(c => c[1]);
          const lngs = coords.map(c => c[0]);
          const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
          const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
          setFormData(prev => ({ ...prev, latitude: centerLat.toFixed(6), longitude: centerLng.toFixed(6) }));
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('AddPlantForm: loadProperties failed:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const identificationPhoto = location.state?.prefilledData?.identificationPhoto;
      const photoToUpload = formData.photo || identificationPhoto;
      const plantPayload = {
        propertyId: formData.propertyId,
        commonName: formData.commonName,
        scientificName: formData.scientificName || null,
        plantType: formData.plantType || null,
        datePlanted: formData.datePlanted || null,
        notes: formData.notes || null,
      };
      if (formData.latitude && formData.longitude) {
        plantPayload.latitude = parseFloat(formData.latitude);
        plantPayload.longitude = parseFloat(formData.longitude);
      }
      const result = await plants.create(plantPayload);
      if (photoToUpload && result?.data?.plant?.id) {
        try {
          const photoForm = new FormData();
          photoForm.append('photo', photoToUpload);
          await photos.uploadForPlant(result.data.plant.id, photoForm);
        } catch (photoError) {
          console.error('AddPlantForm: photo upload failed:', photoError);
        }
      }
      setSubmitting(false);
      setModal({
        isOpen: true, type: 'alert', title: 'Success',
        message: 'Plant added successfully!',
        onConfirm: () => { setModal({ isOpen: false }); navigate('/plants'); }
      });
    } catch (error) {
      console.error('AddPlantForm: create plant failed:', error);
      setSubmitting(false);
      if (error.response?.data?.error === 'PLANT_LIMIT_REACHED') {
        setModal({
          isOpen: true, type: 'alert', title: 'Plant Limit Reached',
          message: 'You\'ve reached the free plan limit of 15 plants. Unlimited plants are coming soon with Yardray Pro — stay tuned!',
          onConfirm: () => setModal({ isOpen: false })
        });
      } else {
        setModal({
          isOpen: true, type: 'alert', title: 'Error',
          message: 'Failed to create plant: ' + (error.response?.data?.error || 'Unknown error'),
          onConfirm: () => setModal({ isOpen: false })
        });
      }
    }
  };

  if (loading) return <div className="add-plant-loading">Loading...</div>;

  if (userProperties.length === 0) {
    return (
      <div className={`add-plant-container${isMobile ? ' add-plant-container--mobile' : ''}`}>
        <h2>Add Plant</h2>
        <div className="add-plant-empty">
          <p>You need to create a property first.</p>
          <button className="add-plant-empty-btn" onClick={() => navigate('/properties')}>
            Go to Properties
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`add-plant-container${isMobile ? ' add-plant-container--mobile' : ''}`}>
      <GardenModal isOpen={modal.isOpen} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} onCancel={() => setModal({ isOpen: false })} />
      <h2>Add New Plant</h2>
      <p className="add-plant-hint">* = required</p>
      <form onSubmit={handleSubmit} className="add-plant-form">

        <div className="add-plant-field">
          <label className="add-plant-label">Property *</label>
          <select
            className="add-plant-input"
            value={formData.propertyId}
            onChange={(e) => {
              const prop = userProperties.find(p => String(p.id) === e.target.value);
              setFormData(prev => ({ ...prev, propertyId: e.target.value, latitude: prop?.latitude || '', longitude: prop?.longitude || '' }));
            }}
            required
          >
            {userProperties.map(prop => <option key={prop.id} value={prop.id}>{prop.name}</option>)}
          </select>
        </div>

        <div className="add-plant-field">
          <label className="add-plant-label">Plant Name *</label>
          <input
            className="add-plant-input"
            type="text"
            value={formData.commonName}
            onChange={(e) => setFormData({...formData, commonName: e.target.value})}
            required
            placeholder="e.g., Tomato, Rose Bush"
          />
        </div>

        <div className="add-plant-field">
          <label className="add-plant-label">Scientific Name</label>
          <input
            className="add-plant-input"
            type="text"
            value={formData.scientificName}
            onChange={(e) => setFormData({...formData, scientificName: e.target.value})}
            placeholder="e.g., Solanum lycopersicum"
          />
        </div>

        <div className="add-plant-field">
          <label className="add-plant-label">Plant Type</label>
          <select
            className="add-plant-input"
            value={formData.plantType}
            onChange={(e) => setFormData({...formData, plantType: e.target.value})}
          >
            <option value="">Select type...</option>
            <option value="flower">Flower</option>
            <option value="fruit">Fruit</option>
            <option value="herb">Herb</option>
            <option value="shrub">Shrub</option>
            <option value="tree">Tree</option>
            <option value="vegetable">Vegetable</option>
            <option value="vine">Vine</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="add-plant-field">
          <label className="add-plant-label">Date Planted</label>
          <input
            className="add-plant-input"
            type="date"
            value={formData.datePlanted}
            onChange={(e) => setFormData({...formData, datePlanted: e.target.value})}
          />
        </div>

        <div className="add-plant-field">
          <label className="add-plant-label">Photo</label>
          {prefilledData?.identificationPhoto && !formData.photo && (
            <div className="add-plant-photo-hint">Photo from plant identifier will be used</div>
          )}
          {formData.photo && (
            <div className="add-plant-photo-hint">Selected: {formData.photo.name}</div>
          )}
          <div className="add-plant-btn-group">
            <label className="add-plant-btn-camera">
              📷 Take Photo
              <input type="file" accept="image/*" capture="environment"
                onChange={(e) => setFormData({...formData, photo: e.target.files[0]})}
                style={{ display: 'none' }} />
            </label>
            <label className="add-plant-btn-gallery">
              🖼️ Choose Photo
              <input type="file" accept="image/*"
                onChange={(e) => setFormData({...formData, photo: e.target.files[0]})}
                style={{ display: 'none' }} />
            </label>
          </div>
        </div>

        <div className="add-plant-field add-plant-field--last">
          <label className="add-plant-label">Notes</label>
          <textarea
            className="add-plant-textarea"
            value={formData.notes}
            onChange={(e) => setFormData({...formData, notes: e.target.value})}
            placeholder="Any additional notes about this plant..."
            rows="3"
          />
        </div>

        <div className="add-plant-actions">
          <button type="submit" disabled={submitting} className="add-plant-btn-submit">
            {submitting ? 'Adding...' : 'Add Plant'}
          </button>
          <button type="button" className="add-plant-btn-cancel" onClick={() => navigate('/plants')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddPlantForm;
